import Moment from 'moment';
import axios from 'axios';
import axiosConfig, { getConfig, getConfigHeadersWithWebSocketNameHeaders } from './config';
import { getCurrentUser, getAccessToken } from '../utils/auth';

export const authHeaders = () => ({ headers: { Authorization: `Bearer ${getAccessToken()}` } });

export const getCreateEventUserTemplates = async (breakTemplateOwnerId) => await axiosConfig.get(`event/templates/${breakTemplateOwnerId}`);
export const getEventStepTeamDistribution = async (templateId) => await axiosConfig.get(`event/team_distribution/${templateId}`);
export const saveEventStep = async (formData) => await axiosConfig.post('event/add', formData);
export const addEventProducts = async (eventId, products, productSelectionId) => await axiosConfig.post(`event/add_products/${eventId}/${productSelectionId}`, products);
export const updateInventory = async (eventId, selectedProducts) => await axiosConfig.put(`event/products/update-inventory/${eventId}`, selectedProducts);
export const getEventStepPresetData = async (
	sportType,
	numberOfTiers,
	numberOfSlots
) => {
	try {
		const res = await axiosConfig.get(`event/templates/${sportType}/${numberOfTiers}/${numberOfSlots}`);
		return res;
	} catch (error) {
		return { status: 400, error: error.response };
	}
};
export const getEventStepInitials = async () => await axiosConfig.get('event/initial');

export const getCreatedEventBreakTemplates = async () => await axiosConfig.get('event/templates');
export const getCreatedEventBreakTemplatesByTiers = async (tiers) => await axiosConfig.get(`event/templates-by-tiers/${tiers}`);
export const getUserTemplatesByUser = async () => await axiosConfig.get('event/templates-by-user');
export const getUserTemplates = async () => {
	try {
		const user = getCurrentUser();
		const res = await axiosConfig.get(`event/templates/${user.id}`);
		return res;
	} catch (error) {
		console.error(error.response);
		return { status: 400, error: error.response };
	}
};

export const getUserTemplatesByTiers = async (tiers) => {
	try {
		const user = getCurrentUser();
		const res = await axiosConfig.get(`event/templates/${user.id}/${tiers}`);
		return res;
	} catch (error) {
		console.error(error.response);
		return { status: 400, error: error.response };
	}
};
export const saveEventTemplate = async (formData) => await axiosConfig.post('event/templates/create', formData);
export const getEventDetails = async (id, liveEvent = true, loadProducts = true) => {
	const config = getConfig();
	config.params = { liveEvent, loadProducts };
	config.headers.common['Cache-Control'] = 'no-cache';
	return await axios.get(`event/${id}`, config);
};
export const updateChatId = async (eventId, chatId) => await axiosConfig.put(`event/room/chat/${eventId}/${chatId}`, null);
export const getEventMessages = async (eventRoomId) => await axiosConfig.get(`event/room/messages/${eventRoomId}`);
export const uploadEventImages = async (files, eventId) => {
	const headers = authHeaders();

	const formData = new FormData();
	files.forEach((f) => {
		formData.append('images', f);
	});
	headers['Content-Type'] = 'multipart/form-data';
	headers.redirect = 'follow';
	headers.mode = 'no-cors';
	return await axiosConfig.post(`event/images/${eventId}`, formData, headers);
};

export const getRoomTokens = async (eventId, nickname) => {
	const token = getAccessToken();
	const data = { eventId };
	if (!token) {
		data.nickname = nickname + (Math.floor(Math.random() * 10) + 1).toString();
		data.chatNickname = nickname;
	}

	return await axiosConfig.post('event/room/tokens', data);
};

export const isEventOwner = async (eventId) => await axiosConfig.get(`event/owner/${eventId}`);
export const startRoom = async (eventId, restart = false) => await axiosConfig.post(`event/room/create/${eventId}/${restart}`, null);
// Difference between `stopRoom`and `stopEventRoom` is this method will stop the event as opposed to stopeventroom where the user is removed from the room
export const stopRoom = async (eventId) => await axiosConfig.put(`event/stop/${eventId}`, null);
export const pauseRoom = async (eventId) => await axiosConfig.put(`event/pause/${eventId}`, null);
export const isEventStopped = async (eventId) => await axiosConfig.get(`event/${eventId}/stopped`);
export const exitRoom = async (eventId, nickname, websocketGroupName) => await axios.delete(`event/room/delete/${eventId}/${nickname}`, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const exitRoomViaFetch = (eventId, nickname) => fetch(`${axiosConfig.defaults.baseURL}event/room/delete/${eventId}/${nickname}`, {
	keepalive: true,
	method: 'DELETE',
	headers: {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${getAccessToken()}`,
	},
});
export const exitRoomByAnonymousToken = async (eventId, token, websocketGroupName) => await axios.delete(`event/room/delete/anonymous/${eventId}/${token}`, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const exitRoomByAnonymousTokenViaFetch = (eventId, token) => fetch(`${axiosConfig.defaults.baseURL}event/room/delete/anonymous/${eventId}/${token}`, {
	keepalive: true,
	method: 'DELETE',
	headers: {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${getAccessToken()}`,
	},
});
export const stopEventRoomViaFetch = (eventId) => fetch(`${axiosConfig.defaults.baseURL}event/stop/${eventId}`, {
	keepalive: true,
	method: 'PUT',
	headers: {
		'Content-Type': 'application/json',
		Authorization: `Bearer ${getAccessToken()}`,
	},
});
export const stopEventRoom = async (eventId) => await axiosConfig.put(`event/stop/${eventId}`, null);
export const getAttendees = async (eventId) => await axiosConfig.get(`event/${eventId}/room/attendees`);
export const isRoomHost = async (eventId) => await axiosConfig.get(`event/is-host/${eventId}`);
export const loadEventBuyers = async (eventId) => await axiosConfig.get(`event/buyers/${eventId}`);
export const messageBuyers = async (requestBody) => await axiosConfig.post('event/buyers/message`', requestBody);
export const verifySeller = async (eventId) => await axiosConfig.get(`event/${eventId}/verify-seller`);
export const addBookmark = async (formData, eventId) => {
	const headers = authHeaders();
	headers['Content-Type'] = 'multipart/form-data';
	headers.redirect = 'follow';
	headers.mode = 'no-cors';

	const currentDate = Moment.utc(new Date()).toJSON();
	return await axiosConfig.post(`event/room/bookmark/${eventId}/${currentDate}`, formData, headers);
};

export const moveQueuePosition = async (eventId, orderId, position, websocketGroupName) => await axios.put(
	`event/ripnship/queue/move/${eventId}/${orderId}/${position}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName)
);

export const exitQueuePosition = async (eventId, userId, websocketGroupName) => await axios.put(
	`event/ripnship/queue/exitturn/${eventId}/${userId}`,
	null,
	getConfigHeadersWithWebSocketNameHeaders(websocketGroupName)
);

export const joinRipNShipQueue = async (eventId, websocketGroupName) => await axios.post(`event/ripnship/join/${eventId}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const initiateEndTurnRequest = async (eventId, websocketGroupName) => await axios.put(`event/ripnship/queue/initiate/endturn/${eventId}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const endTurnRequest = async (eventId, userId, position, linkedEventId, websocketGroupName) => await axios.put(
	`event/${eventId}/ripnship/queue/endturn`,
	{
		linkedEventId,
		userId,
		position
	},
	getConfigHeadersWithWebSocketNameHeaders(websocketGroupName)
);
export const exitQueue = async (eventId, userId) => await axiosConfig.delete(`event/ripnship/queue/exit/${eventId}/${userId}`);
export const requestMic = async (eventId, participantId, twilioIdentity, websocketGroupName) => await axios.post(
	`event/room/mic/request/${eventId}/${participantId}/${twilioIdentity}`,
	null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName)
);
export const requestedMicDecision = async (eventId, approved, users, updateAll, websocketGroupName) => await axios.put(
	'event/room/mic/request/decision', {
		eventId,
		approved,
		users,
		updateAll
	}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName)
);
export const subscribeToTwilioTrack = async (eventId, participantId, trackId) => await axiosConfig.put(
	`event/room/subscribe/track/${eventId}/${participantId}/${trackId}`,
	null
);

export const subscribeToEventRoomTwilioTracks = async (eventId, twilioParticipantId) => await axiosConfig.post(`event/room/subscribe/${eventId}/${twilioParticipantId}`, null);
export const reissueVideoToken = async (eventId, nickname) => await axiosConfig.post('event/room/tokens/reissue', {
	eventId, nickname
});

export const stopMicrophoneAccess = async (eventId) => await axiosConfig.put(
	`event/room/mic/end/${eventId}`,
	null
);
export const createRipNShipEvent = async (ripNShip, eventId) => await axiosConfig.post(`event/ripnship/add/${eventId}`, ripNShip);
export const startRipNShipEvent = async (eventId) => await axiosConfig.post(`event/ripnship/start/${eventId}`, null);
export const getAvailableRipNShip = async () => await axiosConfig.get('event/ripnship/active');
export const planRipNShip = async (eventName, eventTime, parentEventId) => await axiosConfig.post('event/ripnship/plan', {
	eventName, eventTime, parentEventId
});
export const getBookmarkedEvents = async () => await axiosConfig.get('event/bookmarks');
export const bookmarkOrUnbookmarkEvent = async (eventId) => await axiosConfig.post(`event/bookmark/${eventId}`, null);
export const getBookmarkedEvent = async (eventId) => await axiosConfig.get(`event/bookmark/${eventId}`);
export const getEvents = async (listType, pageSize, currentPage, eventType = '', searchValue = '', groupedBySeller = false) => {
	const config = getConfig();
	config.params = {
		type: listType,
		per_page: pageSize,
		page: currentPage,
		eventType,
		searchValue,
		groupedBySeller
	};
	return await axios.get('event/list', config);
};
export const banUserFromEvent = async (eventId, nickname, websocketGroupName) => await axios.put(`event/room/ban/${eventId}/${nickname}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const isUserEligibleForEvent = async (eventId, token = 'undefined') => await axiosConfig.get(`event/room/eligible/${eventId}/${token}`);
export const getQueueOrderPurchasedProducts = async (eventId, userId) => await axiosConfig.get(`event/ripnship/products/${eventId}/${userId}`);
export const getRipNShipEventDetail = async (eventId) => await axiosConfig.get(`event/ripnship/${eventId}`);
export const getQueueOrderPurchasedProductsByQueueOrder = async (queueOrderId) => await axiosConfig.get(`event/ripnship/products/order/${queueOrderId}`);
export const hasUserPurchasedItemsInEvent = async (eventId, userId) => await axiosConfig.get(`event/room/purchased/${eventId}/${userId}`);
export const updateRoomStatusToStarted = async (eventId, sendChatMessage = false) => await axiosConfig.put('event/room/has-started/', {
	eventId,
	sendChatMessage
});
export const hasRoomStatusBeenUpdatedToStarted = async (eventId) => await axiosConfig.get(`event/room/has-been-started/${eventId}`);
export const updateEventRoomVideoTrackId = async (eventId, trackId) => await axiosConfig.put(`event/room/videotrack/${eventId}/${trackId}`, null);
export const getEventRoomVideoTrackId = async (eventId) => await axiosConfig.get(`event/room/videotrack/${eventId}`);
export const updateEventName = async (eventNameBody) => await axiosConfig.put('event/update-name', eventNameBody);
export const updateEventDate = async (eventDateBody) => await axiosConfig.put('event/update-date', eventDateBody);
export const getUndetailedLiveEvents = async () => await axiosConfig.get('event/undetailed/live');
export const showRoomMonitoringTool = async () => await axiosConfig.get('event/room/show-monitoring-tool');
export const getParticipantsFromTwilio = async (eventId) => await axiosConfig.get(`event/room/twilio/participants/${eventId}`);
export const getFailedAttendees = async (eventId) => await axiosConfig.get(`event/room/attendees/failed/${eventId}`);
export const getUndetailedEvent = async (eventId) => await axiosConfig.get(`event/undetailed/${eventId}`);
export const savedEventJoinFailure = async (eventId, reason) => await axiosConfig.post(`event/room/attendees/failed/${eventId}/${reason}`, null);
export const removeUsersFromEvent = async (eventId, userIds, anonymousTokens, websocketGroupName) => await axios.put('event/room/remove', { eventId, userIds, tokens: anonymousTokens }, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const getTwilioMonitoringToolToken = async (eventId) => await axiosConfig.get(`event/room/monitoring-tool/token/${eventId}`);
export const generateTwilioMonitoringToolToken = async (eventId) => await axiosConfig.put(`event/room/monitoring-tool/token/${eventId}`);
export const saveSubscribedTrack = async (eventId, nickname, trackId, kind, identity) => await axiosConfig.post(`event/room/subscribe/${eventId}/${nickname}/${trackId}/${kind}/${identity}`);
export const saveSubscribedTrackAnonymousUser = async (eventId, token, trackId, kind, identity) => await axiosConfig.post(`event/room/subscribe/anonymous/${eventId}/${token}/${trackId}/${kind}/${identity}`);
export const updateEventRoomDate = async (eventId) => await axiosConfig.put('event/room/date', { eventId, startedDate: Moment.utc(new Date()).toJSON() });
export const removeMonitoringToolTwilioToken = async (eventId) => await axiosConfig.delete(`event/room/monitoring-tool/${eventId}`);
export const getEventReport = async (eventId, type = 'excel', returnType = 'base64') => await axiosConfig.get(`event/report/${eventId}/${type}/${returnType}`);
export const sendEventReportEmail = async (eventId, base64) => await axiosConfig.post('event/report/email', { eventId, bodyBase64: base64 });
export const canRestartRoom = async (eventId) => await axiosConfig.get(`event/room/can-restart/${eventId}`);
export const cancelEvent = async (eventId, refund = true) => await axiosConfig.put('event/cancel', {
	eventId,
	refund
});
export const closeRipNShipQueue = async (eventId, hasFutureEvent, eventEnding, websocketGroupName) => await axios.put(`event/ripnship/close-line/${eventId}/${eventEnding}/${hasFutureEvent}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const activeLineDecideItemStatus = async (eventId, keepItems = false, websocketGroupName) => await axios.put(`event/ripnship/active-line/keep-items/${eventId}/${keepItems}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const isRipNShipQueueClosed = async (eventId) => await axiosConfig.get(`event/ripnship/line-closed/${eventId}`);
export const addBuyersToNextLineInFutureEvent = async (eventId) => await axiosConfig.post(`event/ripnship/buyers/add-to-line/next-event/${eventId}`);
export const shouldShowKeepItemsOptionsInRipNShip = async (eventId) => await axiosConfig.get(`event/ripnship/${eventId}/current-line/show-keep-items-options`);
export const openRipNShipQueueLine = async (eventId, websocketGroupName) => await axios.put(`event/ripnship/open-line/${eventId}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const generateEventRequestLink = async (eventId, emailAddress) => await axiosConfig.post('event/request-link', {
	eventId,
	emailAddress
});
export const getEventFeed = async (eventId) => await axiosConfig.get(`event/feed/${eventId}`);
export const processRefundsOnRipNShipNoContinuation = async (eventId, websocketGroupName) => await axios.put(`event/ripnship/no-continuation/${eventId}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const sellerHasNoLiveEventInProgress = async (excludedEventId = '') => {
	let apiMethod = 'event/no-live-event-in-progress';
	if (excludedEventId && excludedEventId.length) {
		apiMethod += `/${excludedEventId}`;
	}
	return await axiosConfig.get(apiMethod);
};
export const revertEventStartedStatus = async (eventId) => await axiosConfig.put(`event/room/revert-status/${eventId}`);
export const getRipNShipActiveProducts = async (eventId, page = null, perPage = null, searchQuery = '', sortBy = '', sortByDirection = '') => {
	const config = getConfig();
	if (page != null && perPage != null) {
		config.params = {
			page, per_page: perPage, searchValue: searchQuery, sort_by: sortBy, sort_direction: sortByDirection
		};
	}
	return await axios.get(`event/ripnship/products/${eventId}`, config);
};
export const getAllEventProducts = async (eventId) => await axiosConfig.get(`event/products/${eventId}/all`);
export const deleteEventProduct = async (eventProductId) => await axiosConfig.delete(`event/product/remove/${eventProductId}`);
export const addTwilioParticipantInfoToEventAttendee = async (eventId, twilioRoomIdentity, twilioParticipantId, websocketGroupName) => await axios.put('event/room/add-twilio-participant-info', {
	eventId,
	twilioParticipantId,
	twilioRoomIdentity,
}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));

export const unsubcribeAudioTracksFromEvent = async (eventId, twilioParticipantId, expireActiveMicTracksAsWell = false, websocketGroupName) => await axios.put('event/room/tracks/unsubscribe', {
	eventId,
	twilioParticipantId,
	kind: 'audio',
	expireActiveMic: expireActiveMicTracksAsWell
}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const cancelMicRequest = async (eventId, twilioParticipantId, websocketGroupName) => await axios.put(`event/room/mic/cancel/${eventId}/${twilioParticipantId}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const approveMicRequestByBuyer = async (eventId, oldTwilioParticipantId, newTwilioParticipantId, twilioTrackId, websocketGroupName) => await axios.put('event/room/mic/approved-mic-info', {
	eventId, oldTwilioParticipantId, newTwilioParticipantId, twilioTrackId
}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const updateMutedMicStatus = async (eventId, users, muted, initiatedBySeller, muteOrUnmuteAll, websocketGroupName) => await axios.put('event/room/mic/mute-status',
	{
		eventId,
		muted,
		initiatedBySeller,
		users,
		muteOrUnmuteAll
	}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const closeActiveMic = async (eventId, twilioParticipantIds, initiatedBySeller, closeAll, websocketGroupName) => await axios.put('event/room/mic/close', {
	eventId,
	initiatedBySeller,
	twilioParticipantIds,
	closeAllMics: closeAll
}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const expireActiveMicSessionsOrRequests = async (eventId, twilioIdentity, websocketGroupName) => await axios.put(`event/room/mic/expire/requests-and-active-mic-sessions/${eventId}/${twilioIdentity}`, null, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const deleteMicRequest = async (eventId, twilioParticipantId, websocketGroupName, sendMicCancelMessage) => await axios.delete(`event/room/mic/request/delete/${eventId}/${twilioParticipantId}/${sendMicCancelMessage}`, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const getEventsEligibleToLink = async (excludedEventId) => await axiosConfig.get(`event/eligible-events-to-link/${excludedEventId}`);
export const linkEvents = async (mainEventId, linked, linkedEventIds, removeOtherLinkedEvents, updateLinkedEventsStatus) => await axiosConfig.post('event/link', {
	mainEventId,
	linked,
	linkedEventIds,
	removeOtherLinkedEvents,
	updateLinkedEventsStatus
});
export const getMainEventId = async (eventId) => await axiosConfig.get(`event/${eventId}/main-event-id`);
export const updateStartedStatusOfCreatedAndLinkedEvent = async (eventId) => await axiosConfig.put(`event/${eventId}/start-created-event-via-main-event`);
export const updateEventProductReservedQuantityByEvent = async (eventId, eventProductId, quantity) => await axiosConfig.put(`event/${eventId}/products/${eventProductId}/quantity/update/${quantity}`);
export const getVideoRooms = async (eventId) => await axiosConfig.get(`event/${eventId}/room/video`);
export const getLiveEventParticipantsInfo = async (eventId, participants) => await axiosConfig.post(`event/${eventId}/room/users`, {
	twilioParticipants: participants
});
export const updateNewLiveEventHostParticipant = async (eventId, hostIdentity, websocketGroupName) => await axios.post(`event/${eventId}/room/new-host`, {
	identity: hostIdentity
}, getConfigHeadersWithWebSocketNameHeaders(websocketGroupName));
export const getCurrentLiveEventHostIdentity = async (eventId) => await axiosConfig.get(`event/${eventId}/room/host-identity`);
export const excludeIdentityFromLiveEventChatNotifications = async (eventId, identity) => await axiosConfig.post(`event/${eventId}/room/exclude/chat-notifications`, {
	eventId,
	identity
});
export const getRipNShipBuyersQueue = async (eventId) => await axiosConfig.get(`event/ripnship/queue/${eventId}`);
export const liveEventWithoutHost = async () => await axiosConfig.get('event/room/host-has-disconnected');
export const resolveMicConversationMessage = async (eventId, twilioParticipantId, approved) => await axiosConfig.put('event/room/resolved-mic-request-conversation', {
	eventId,
	twilioParticipantId,
	approved
});

export const logChatEventRoomError = async (eventId, errorMessage) => await axiosConfig.post('event/room/chat/log-error', {
	eventId,
	error: errorMessage
});

export const copyEvent = async (eventIdToCopyFrom, eventTime, eventName, numberOfCopies) => await axiosConfig.post('event/copy', {
	eventId: eventIdToCopyFrom,	eventTime, eventName, numberOfCopies
});
